/**
 * Date utility functions for the application
 */

import {
  endOfDay,
  format,
  formatDistanceToNow,
  formatISO,
  isAfter,
  isBefore,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";
import { de, enUS, nl } from "date-fns/locale";

/**
 * Checks if a date is in the current month
 */
export const isDateInCurrentMonth = (date: Date | null): boolean => {
  if (!date) return false;

  const now = new Date();
  // Compare only year and month, time is irrelevant
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  );
};

/**
 * Gets the last day of next month
 */
export const getLastDayOfNextMonth = (): Date => {
  const today = new Date();
  // Day 0 of the month after next rolls back to the last day of next month. Going through
  // setMonth() instead would overflow on a 31st (Sep 31 -> Oct 1) and land a month late.
  return new Date(today.getFullYear(), today.getMonth() + 2, 0);
};

/**
 * Gets the first day of next month
 */
export const getFirstDayOfNextMonth = (): Date => {
  const today = new Date();
  today.setMonth(today.getMonth() + 1);
  today.setDate(1);
  return today;
};

/**
 * Gets today's date set to the start of the day (midnight)
 */
export const getToday = (): Date => {
  return new Date();
};

export const getNextDay = (date: Date): Date => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  return nextDay;
};

export function isBeforeToday(date?: Date | string | null): boolean {
  if (!date) return false;

  const targetDate = new Date(date);
  const today = new Date();

  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return targetDate < today;
}

export function isToday(date?: Date | string | null): boolean {
  if (!date) return false;

  const targetDate = new Date(date);
  const today = new Date();

  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return targetDate.toDateString() === today.toDateString();
}

export const getMinEndDate = (startDate: string | null, isEdit: boolean) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!startDate) return today;

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  if (isEdit && start < today) {
    return today;
  }

  const minDate = getNextDay(start);
  return minDate;
};

/**
 * Gets the number of days remaining in the month from the given date (inclusive)
 */
export const getDaysRemainingInMonth = (date: Date): number => {
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const daysRemaining = lastDayOfMonth.getDate() - date.getDate() + 1;
  return daysRemaining;
};

/**
 * Prorated first-month budget: the monthly budget scaled by the share of the start month the
 * campaign is live, counting the start day itself (inclusive). A start date on the 1st returns
 * the full monthly budget. rounded-lg to 2 decimals.
 *
 * Mirrors the backend SharedCode/Utils/CampaignDateUtils.ProRataFirstMonthBudget so the read-only
 * estimate shown in the form matches the amount the allocator persists.
 *
 * @example €300, June (30 days), start June 20 → 30 - 20 + 1 = 11 days → 300 / 30 × 11 = €110.
 */
export const getProRataFirstMonthBudget = (
  monthlyBudget: number,
  startDate: Date,
): number => {
  const daysInMonth = new Date(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    0,
  ).getDate();
  const remainingDaysInclusive = getDaysRemainingInMonth(startDate);
  const prorated = (monthlyBudget / daysInMonth) * remainingDaysInclusive;
  return Math.round(prorated * 100) / 100;
};

export function stringToDate(value: string | undefined): Date | undefined {
  if (!value || value.trim() === "") return undefined;
  try {
    return parseISO(value);
  } catch {
    return undefined;
  }
}

export function dateToString(date: Date | undefined): string | undefined {
  return date ? formatISO(date, { representation: "date" }) : undefined;
}

/** Parse YYYY-MM-DD as local date (avoids UTC shift). */
export function parseLocalDateOnly(
  value: string | null | undefined,
): Date | undefined {
  if (!value?.trim()) return undefined;
  const d = parse(value.trim(), "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

/** Format date as local YYYY-MM-DD (avoids UTC shift). */
export function formatLocalDateOnly(date: Date | undefined): string {
  return date ? format(date, "yyyy-MM-dd") : "";
}

/** Clamps a date into the inclusive [min, max] window (returns min/max when outside). */
export function clampDate(date: Date, min: Date, max: Date): Date {
  if (isBefore(date, min)) return min;
  if (isAfter(date, max)) return max;
  return date;
}

/** Returns UTC ISO string for the start of the given local date (00:00:00 local → UTC). */
export function localStartOfDayUtc(date: Date): string {
  return startOfDay(date).toISOString();
}

/** Returns UTC ISO string for the end of the given local date (23:59:59.999 local → UTC). */
export function localEndOfDayUtc(date: Date): string {
  return endOfDay(date).toISOString();
}

/**
 * Gets the date-fns locale object based on language code
 * @param locale - Language code ('en', 'de', 'nl')
 * @returns date-fns locale object
 */
export function getDateFnsLocale(locale?: string) {
  switch (locale?.toLowerCase()) {
  case "de":
    return de;
  case "nl":
    return nl;
  case "en":
  default:
    return enUS;
  }
}

/**
 * Formats a date as relative time (e.g., "2 days ago", "one month ago")
 * @param date - Date to format (Date, string, or null)
 * @param locale - Language code for locale ('en', 'de', 'nl')
 * @returns Relative time string in the specified locale
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
  locale?: string,
): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";

  const dateFnsLocale = getDateFnsLocale(locale);
  return formatDistanceToNow(dateObj, {
    addSuffix: true,
    locale: dateFnsLocale,
  });
}

/**
 * Formats a date for display in a tooltip (e.g., "January 15, 2024 at 3:45 PM")
 * @param date - Date to format (Date, string, or null)
 * @param locale - Language code for locale ('en', 'de', 'nl')
 * @returns Formatted date string in the specified locale
 */
export function formatDateForTooltip(
  date: Date | string | null | undefined,
  locale?: string,
): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";

  const dateFnsLocale = getDateFnsLocale(locale);
  // PPp = long localized date + time (e.g., "January 15, 2024 at 3:45 PM")
  return format(dateObj, "PPp", { locale: dateFnsLocale });
}

/**
 * Formats a date for display in a tooltip, date only (e.g., "January 15, 2024")
 * @param date - Date to format (Date, string, or null)
 * @param locale - Language code for locale ('en', 'de', 'nl')
 * @returns Formatted date string in the specified locale
 */
export function formatDateOnlyForTooltip(
  date: Date | string | null | undefined,
  locale?: string,
): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";

  const dateFnsLocale = getDateFnsLocale(locale);
  return format(dateObj, "PP", { locale: dateFnsLocale });
}
